import React, { useState } from "react";
import { motion } from "framer-motion";
import { NavLink, useNavigate } from "react-router-dom";
import "./login.css";
import axios from "axios";

const API_BASE = "https://api-store.crescentworld.com/api/admin";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ mobile: "", password: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const validate = () => {
    const newErrors = {};
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setServerError("");

    try {
      console.log("Attempting login with:", { mobile: formData.mobile });

      const res = await axios.post(`${API_BASE}/login`, {
        mobile: formData.mobile.trim(),
        password: formData.password
      });

      console.log("Login response:", res.data);

      const { token, admin } = res.data;

      if (!token) {
        throw new Error("No token received");
      }

      // Store admin data
      localStorage.setItem("adminToken", token);
      localStorage.setItem("adminData", JSON.stringify(admin));
      localStorage.setItem("role", "admin");

      console.log("Login successful, redirecting to dashboard");

      // Redirect to dashboard
      navigate("/admin/dashboard");

    } catch (error) {
      console.error("Login error:", error);
      console.error("Error response:", error.response);

      if (error.response) {
        // Server responded with error
        switch (error.response.status) {
          case 400:
            setServerError(error.response?.data?.message || "Invalid credentials");
            break;
          case 401:
            setServerError(error.response?.data?.message || "Session expired or account logged in elsewhere");
            break;
          case 404:
            setServerError("Admin not found. Please check your mobile number.");
            break;
          default:
            setServerError(error.response?.data?.message || "Login failed. Please try again.");
        }
      } else if (error.request) {
        // Request was made but no response
        setServerError("Cannot connect to server. Please check if backend is running.");
      } else {
        // Something else happened
        setServerError(error.message || "Login failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Forgot Password - Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!forgotMobile) {
      setForgotError("Please enter your mobile number");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const res = await axios.post(`${API_BASE}/forgot-password`, { mobile: forgotMobile });
      setForgotMessage(res.data.message);
      setForgotStep(2);
    } catch (error) {
      setForgotError(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      setForgotError("Please enter the OTP");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const res = await axios.post(`${API_BASE}/verify-otp`, {
        mobile: forgotMobile,
        otp: otp
      });
      setResetToken(res.data.resetToken);
      setForgotMessage("OTP verified successfully!");
      setForgotStep(3);
    } catch (error) {
      setForgotError(error.response?.data?.message || "Invalid OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setForgotError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      await axios.post(`${API_BASE}/reset-password`, {
        resetToken,
        newPassword
      });

      setForgotMessage("Password reset successfully! Please login with your new password.");

      setTimeout(() => {
        setShowForgotPassword(false);
        resetForgotState();
      }, 2000);

    } catch (error) {
      setForgotError(error.response?.data?.message || "Failed to reset password");
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setShowForgotPassword(false);
    setForgotMobile("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setResetToken("");
    setForgotStep(1);
    setForgotMessage("");
    setForgotError("");
    setForgotLoading(false);
  };

  return (
    <div className="cw-login-page">
      <div className="cw-login-glow cw-login-glow-one" />
      <div className="cw-login-glow cw-login-glow-two" />

      <motion.main
        className="cw-login-shell"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: "easeOut" }}
      >
        <section className="cw-login-brand-panel">
          <div className="cw-brand-top">
            {/* <div className="cw-brand-mark">C</div>
            <div>
              <div className="cw-brand-name">CRESCENT WORLD</div>
              <div className="cw-brand-caption">ADMINISTRATION PORTAL</div>
            </div> */}
            <img src="./images/logo.png" alt="Crescent World Logo" className="cw-brand-logo w-50" />
          </div>

          <div className="cw-brand-content">
            <span className="cw-eyebrow"><span /> CONTROL. MANAGE. GROW.</span>
            <h1>Everything you need,<br /><em>all in one place.</em></h1>
            <p>
              Welcome to your central workspace. Manage your store operations
              with clarity, confidence, and complete control.
            </p>
            <div className="cw-brand-points">
              <div className="cw-brand-point"><span className="cw-point-icon">✓</span><span>Secure administrator access</span></div>
              <div className="cw-brand-point"><span className="cw-point-icon">✓</span><span>One place for your operations</span></div>
              <div className="cw-brand-point"><span className="cw-point-icon">✓</span><span>Designed for a seamless workflow</span></div>
            </div>
          </div>

          <div className="cw-brand-footer">
            <span>© {new Date().getFullYear()} Crescent World - All rights reserved</span>
            {/* <span className="cw-brand-footer-line" /> */}
            <span>Admin workspace</span>
          </div>
          <div className="cw-brand-decoration cw-decoration-one" />
          <div className="cw-brand-decoration cw-decoration-two" />
        </section>

        <section className="cw-login-form-panel">
          <div className="cw-mobile-brand">
            <div className="cw-brand-mark">C</div>
            <span>CRESCENT WORLD</span>
          </div>

          <div className="cw-form-heading">
            <span className="cw-form-kicker">WELCOME BACK</span>
            <h2>Admin sign in</h2>
            <p>Enter your credentials to access your dashboard.</p>
          </div>

          {serverError && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="cw-alert cw-alert-error"
              role="alert"
            >
              <span className="cw-alert-symbol">!</span>
              {serverError}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="cw-login-form">
            <div className="cw-field-group">
              <label htmlFor="admin-mobile">Mobile number</label>
              <div className={`cw-input-wrap ${errors.mobile ? "cw-input-invalid" : ""}`}>
                <span className="cw-input-icon">☎</span>
                <input
                  id="admin-mobile"
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter your mobile number"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
              {errors.mobile && <div className="cw-field-error">{errors.mobile}</div>}
            </div>

            <div className="cw-field-group">
              <label htmlFor="admin-password">Password</label>
              <div className={`cw-input-wrap ${errors.password ? "cw-input-invalid" : ""}`}>
                <span className="cw-input-icon cw-lock-icon">🔒︎</span>
                <input
                  id="admin-password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
              {errors.password && <div className="cw-field-error">{errors.password}</div>}
            </div>

            <div className="cw-forgot-row">
              <button
                type="button"
                className="cw-text-button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotError("");
                  setForgotMessage("");
                }}
              >
                Forgot password?
              </button>
            </div>

            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
              className="cw-submit-button"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in to dashboard</span>
                  <span className="cw-button-arrow">→</span>
                </>
              )}
            </motion.button>
          </form>

          <div className="cw-login-divider"><span>OR</span></div>
          <div className="cw-user-login">
            Not an admin?
            <NavLink to="/"> Go to user login <span>↗</span></NavLink>
          </div>

          <div className="cw-security-note">
            <span className="cw-security-icon">▣</span>
            <span>Your connection is protected with secure access.</span>
          </div>
        </section>
      </motion.main>

      {showForgotPassword && (
        <div
          className="cw-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) resetForgotState();
          }}
        >
          <motion.div
            className="cw-reset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cw-reset-title"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25 }}
          >
            <div className="cw-modal-header">
              <div className="cw-modal-icon">↻</div>
              <button type="button" className="cw-modal-close" onClick={resetForgotState} aria-label="Close">×</button>
              <span className="cw-form-kicker">ACCOUNT RECOVERY</span>
              <h3 id="cw-reset-title">Reset your password</h3>
              <p>Follow the steps below to securely regain access.</p>
              <div className="cw-stepper">
                {[1, 2, 3].map((step) => (
                  <div className={`cw-step ${forgotStep >= step ? "active" : ""}`} key={step}>
                    <span>{step}</span>
                    <small>{step === 1 ? "Mobile" : step === 2 ? "Verify" : "Reset"}</small>
                  </div>
                ))}
              </div>
            </div>

            <div className="cw-modal-body">
              {forgotMessage && <div className="cw-alert cw-alert-success">{forgotMessage}</div>}
              {forgotError && <div className="cw-alert cw-alert-error">{forgotError}</div>}

              {forgotStep === 1 && (
                <form onSubmit={handleSendOTP}>
                  <div className="cw-field-group">
                    <label htmlFor="forgot-mobile">Registered mobile number</label>
                    <div className="cw-input-wrap">
                      <span className="cw-input-icon">☎</span>
                      <input
                        id="forgot-mobile"
                        type="tel"
                        value={forgotMobile}
                        onChange={(e) => setForgotMobile(e.target.value)}
                        placeholder="Enter your registered mobile number"
                        autoComplete="tel"
                        required
                      />
                    </div>
                    <small className="cw-helper-text">We’ll send a 6-digit OTP to this mobile number.</small>
                  </div>
                  <button type="submit" className="cw-submit-button" disabled={forgotLoading}>
                    {forgotLoading ? "Sending OTP..." : "Send OTP"} <span className="cw-button-arrow">→</span>
                  </button>
                </form>
              )}

              {forgotStep === 2 && (
                <form onSubmit={handleVerifyOTP}>
                  <div className="cw-field-group">
                    <label htmlFor="forgot-otp">Enter verification code</label>
                    <div className="cw-input-wrap">
                      <span className="cw-input-icon">#</span>
                      <input
                        id="forgot-otp"
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter 6-digit OTP"
                        maxLength="6"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                      />
                    </div>
                    <small className="cw-helper-text">OTP sent to {forgotMobile}</small>
                  </div>
                  <button type="submit" className="cw-submit-button" disabled={forgotLoading}>
                    {forgotLoading ? "Verifying..." : "Verify OTP"} <span className="cw-button-arrow">→</span>
                  </button>
                  <button type="button" className="cw-secondary-button" onClick={() => { setForgotStep(1); setForgotError(""); setForgotMessage(""); }}>
                    Back to mobile number
                  </button>
                </form>
              )}

              {forgotStep === 3 && (
                <form onSubmit={handleResetPassword}>
                  <div className="cw-field-group">
                    <label htmlFor="new-password">New password</label>
                    <div className="cw-input-wrap">
                      <span className="cw-input-icon cw-lock-icon">⌑</span>
                      <input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>
                  <div className="cw-field-group">
                    <label htmlFor="confirm-password">Confirm new password</label>
                    <div className="cw-input-wrap">
                      <span className="cw-input-icon cw-lock-icon">⌑</span>
                      <input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>
                  <button type="submit" className="cw-submit-button" disabled={forgotLoading}>
                    {forgotLoading ? "Resetting..." : "Update password"} <span className="cw-button-arrow">→</span>
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
